import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/lib/database.types";
import { normalizeUsernameKey } from "@/lib/username";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type LoginIdentifier =
  | { kind: "username"; raw: string; key: string }
  | { kind: "email"; raw: string; key: string };

export type LoginUserRow = {
  id: string;
  hashed_pin: string;
  username_normalized: string | null;
};

/** Parses the login field — accepts username or email (login only). */
export function parseLoginIdentifier(input: string): LoginIdentifier {
  const raw = input.trim();
  if (EMAIL_PATTERN.test(raw)) {
    return { kind: "email", raw, key: raw.toLowerCase() };
  }
  return { kind: "username", raw, key: normalizeUsernameKey(raw) };
}

export function isValidLoginIdentifier(input: string): boolean {
  const trimmed = input.trim();
  if (trimmed.length < 3) return false;
  if (EMAIL_PATTERN.test(trimmed)) return trimmed.length <= 320;
  return trimmed.length <= 50;
}

export function loginRateLimitKey(identifier: LoginIdentifier): string {
  return identifier.key;
}

/** Resolve account for password login by username or recovery email. */
export async function findUserForPasswordLogin(
  db: SupabaseClient<Database>,
  identifier: LoginIdentifier,
): Promise<LoginUserRow | null> {
  if (identifier.kind === "email") {
    const { data: rows, error } = await db
      .from("codex_users")
      .select("id, hashed_pin, username_normalized, email")
      .ilike("email", identifier.key)
      .limit(2);

    if (error || !rows?.length) return null;
    if (rows.length > 1) return null;

    const row = rows[0]!;
    if (!row.email || row.email.trim().toLowerCase() !== identifier.key) {
      return null;
    }

    return {
      id: row.id,
      hashed_pin: row.hashed_pin,
      username_normalized: row.username_normalized,
    };
  }

  const { data: byNorm } = await db
    .from("codex_users")
    .select("id, hashed_pin, username_normalized")
    .eq("username_normalized", identifier.key)
    .maybeSingle();

  if (byNorm) return byNorm;

  const esc = identifier.key
    .replace(/\\/g, "\\\\")
    .replace(/%/g, "\\%")
    .replace(/_/g, "\\_");

  const { data: byDisplay } = await db
    .from("codex_users")
    .select("id, hashed_pin, username_normalized")
    .ilike("username", esc)
    .maybeSingle();

  return byDisplay ?? null;
}
